import { createAsyncThunk } from "@reduxjs/toolkit";
import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  addToJobQueue,
  clearJobQueue,
  disableNavBarAndSideBar,
  removeFromJobQueue,
  setHasUnsavedChanges,
  showInlineAlert,
} from "slices/app/appSlice";
import { awaitJob, download, pollJobStatus } from "utils/jobs/jobUtils";

import {
  selectHiddenParagraphs,
  selectHiddenTerms,
  selectIsByParagraph,
  selectReportTerms,
  selectReportTermsData,
} from "./selectors";
import {
  setCategories,
  setIsLoadingCategories,
  setIsReviewMode,
  setParagraphs,
  setReport,
  setSections,
  setSentences,
  setTerms,
} from "./shreddingDetailSlice";

const isDictionaryStale = (error) =>
  error.status === 409 &&
  error.response?.data?.error.includes("We need to refresh this view.");

export const fetchReportSections = (baseUrl, reportId) => async (dispatch) => {
  dispatch(addToJobQueue("fetchingReportSections"));
  try {
    const rsp = await localhost.get(`${baseUrl}/report/${reportId}/sections`);

    dispatch(setSections(rsp?.data || []));
  } catch (error) {
    if (isDictionaryStale(error)) {
      dispatch(showRefreshModal(error, baseUrl, reportId));
    } else {
      dispatch(showErrorModal(error));
    }
  } finally {
    dispatch(removeFromJobQueue("fetchingReportSections"));
  }
};

export const fetchReportCategories =
  (baseUrl, reportId) => async (dispatch, getState) => {
    dispatch(addToJobQueue("fetchingReportCatgories"));
    dispatch(setIsLoadingCategories(true));
    const state = getState();
    const hiddenTerms = selectHiddenTerms(state);
    let result = [];

    try {
      const url = `${baseUrl}/report/${reportId}/categories`;
      const rsp = await localhost.post(url, { hiddenTerms });
      const [categories] = rsp?.data || [];
      dispatch(setCategories(categories));

      result = result.concat(categories?.dict?.cats || []);
    } catch (error) {
      if (isDictionaryStale(error)) {
        dispatch(showRefreshModal(error, baseUrl, reportId));
      } else {
        dispatch(showErrorModal(error));
      }
    } finally {
      dispatch(removeFromJobQueue("fetchingReportCatgories"));
      dispatch(setIsLoadingCategories(false));
    }
    return result;
  };

export const fetchReport =
  (baseUrl, { reportId }) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("fetchingShreddingReport"));
    const state = getState();
    const hiddenParagraphs = selectHiddenParagraphs(state);

    try {
      const response = await localhost.post(`${baseUrl}/report/${reportId}`, {
        hiddenParagraphs,
      });

      if (response.data.report.isLockedByAnotherUser) {
        dispatch(
          showInlineAlert({
            variant: alertVariants.warning,
            text: `You cannot perform any actions with this report, as someone else (${response.data.report.isLockedBy}) is reviewing it`,
          })
        );
      }

      dispatch(setReport(response?.data.report || null));
      dispatch(setSentences(response.data.sentenceTermInstances || []));
      dispatch(setParagraphs(response.data.paragraphTermInstances || []));
    } catch (error) {
      const { status } = error;
      const isDictionaryStale = status === 409;
      if (isDictionaryStale) {
        dispatch(showRefreshModal(error, baseUrl, reportId));
      } else {
        dispatch(showErrorModal(error));
      }
    } finally {
      dispatch(removeFromJobQueue("fetchingShreddingReport"));
    }
  };

export const fetchReportTermInstances =
  (baseUrl, { reportId, isByParagraph = true } = {}) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("fetchingTermInstances"));
    const state = getState();
    const hiddenParagraphs = selectHiddenParagraphs(state);

    try {
      const rsp = await localhost.post(
        `${baseUrl}/report/${reportId}/termInstances`,
        { byParagraph: isByParagraph, hiddenParagraphs }
      );
      dispatch(setTerms(rsp?.data || []));
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(removeFromJobQueue("fetchingTermInstances"));
    }
  };

const showRefreshModal = (error, baseApiPath, reportId) => (dispatch) => {
  const errorMessage = error.response?.data?.error;
  dispatch(
    showModal(modalTypes.CONFIRMATION, {
      headerText: "Confirm Action",
      bodyText: errorMessage,
      okButton: {
        text: "Refresh",
        onOk: async () => {
          await dispatch(refreshReportStatistics(baseApiPath, reportId));
          dispatch(hideModal(modalTypes.CONFIRMATION));
          globalThis.location.reload();
        },
      },
    })
  );
};

export const refreshReportStatistics =
  (baseUrl, reportId) => async (dispatch) => {
    const url = `${baseUrl}/report/${reportId}/refreshStats`;

    try {
      await localhost.post(url);
      dispatch(fetchReportCategories(baseUrl, reportId));
    } catch (err) {
      console.error(err);
    }
  };

export const getTermInstanceByTermId = (termId) => (_, getState) => {
  const state = getState();
  const instances = selectReportTermsData(state);

  return (instances || []).findIndex(({ terms = [] }) =>
    terms.includes(termId)
  );
};

export const getTermInstanceByLocation = (value) => (_, getState) => {
  const state = getState();
  const instances = selectReportTermsData(state);

  return (instances || []).findIndex(({ location }) => location === value);
};

export const saveShreddingReport =
  ({ baseApiPath, reportId }) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("saveShreddingReportReview"));
    const state = getState();
    const termInstances = selectReportTerms(state);
    const isByParagraph = selectIsByParagraph(state);

    try {
      const url = `${baseApiPath}/report/${reportId}/save`;

      const updates = termInstances.map((term) => {
        const update = {
          paragraphId: term.paragraphId,
          criticality: term.criticality,
        };
        if (!isByParagraph) {
          update.sentenceIndex = term.sentenceIndex;
        }
        return update;
      });

      const response = await localhost.put(url, {
        byParagraph: isByParagraph,
        paragraphUpdates: updates,
      });

      if (response?.status === 200) {
        dispatch(setIsReviewMode(false));
        dispatch(disableNavBarAndSideBar(false));
        dispatch(setHasUnsavedChanges(false));
        dispatch(fetchReport(baseApiPath, { reportId }));
      }

      return response;
    } catch (error) {
      if (error.status === 423) {
        const hiddenParagraphs = selectHiddenParagraphs(state);
        const isByParagraph = selectIsByParagraph(state);

        const params = { isByParagraph, hiddenParagraphs, reportId };
        dispatch(fetchReportTermInstances(baseApiPath, params));
      }
      dispatch(showErrorModal(error));
      return null;
    } finally {
      dispatch(removeFromJobQueue("saveShreddingReportReview"));
    }
  };

export const saveAndCompleteShreddingReport =
  ({ baseApiPath, reportId }) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("completeShreddingReportReview"));
    const state = getState();
    const termInstances = selectReportTerms(state);
    const isByParagraph = selectIsByParagraph(state);

    try {
      const url = `${baseApiPath}/report/${reportId}/complete`;

      const updates = termInstances.map((term) => {
        const update = {
          paragraphId: term.paragraphId,
          criticality: term.criticality,
        };

        if (!isByParagraph) {
          update.sentenceIndex = term.sentenceIndex;
        }
        return update;
      });

      const response = await localhost.put(url, {
        byParagraph: isByParagraph,
        paragraphUpdates: updates,
      });

      if (response.status === 200) {
        dispatch(setIsReviewMode(false));
        dispatch(disableNavBarAndSideBar(false));
        dispatch(setHasUnsavedChanges(false));
        dispatch(fetchReport(baseApiPath, { reportId }));
      }

      return response;
    } catch (error) {
      if (error.status === 423) {
        const hiddenParagraphs = selectHiddenParagraphs(state);
        const isByParagraph = selectIsByParagraph(state);

        const params = { isByParagraph, hiddenParagraphs, reportId };
        dispatch(fetchReportTermInstances(baseApiPath, params));
      }
      dispatch(showErrorModal(error));
      return null;
    } finally {
      dispatch(removeFromJobQueue("completeShreddingReportReview"));
    }
  };

export const startShreddingReportReview =
  ({ baseApiPath, reportId }) =>
  async (dispatch) => {
    dispatch(addToJobQueue("lockShreddingReport"));

    const url = `${baseApiPath}/report/${reportId}/lock`;

    try {
      const response = await localhost.get(url);

      if (response?.status === 200) {
        dispatch(setIsReviewMode(true));
        dispatch(disableNavBarAndSideBar(true));
      }

      return response;
    } catch (error) {
      if (error.status === 423) {
        dispatch(
          showInlineAlert({
            variant: alertVariants.warning,
            text: error.response.data,
          })
        );
      } else {
        dispatch(showErrorModal(error, "Failed to lock report"));
      }
      return null;
    } finally {
      dispatch(removeFromJobQueue("lockShreddingReport"));
    }
  };

export const cancelShreddingReportReview =
  ({ baseApiPath, reportId }) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("cancelShreddingReportReview"));
    const state = getState();

    const hiddenParagraphs = selectHiddenParagraphs(state);
    const params = { hiddenParagraphs, reportId };

    try {
      const url = `${baseApiPath}/report/${reportId}/unlock`;

      const response = await localhost.get(url);

      if (response.status === 200) {
        dispatch(setIsReviewMode(false));
        dispatch(disableNavBarAndSideBar(false));
        dispatch(setHasUnsavedChanges(false));
        dispatch(fetchReport(baseApiPath, params));
      }
    } catch (error) {
      if (error.status === 423) {
        dispatch(fetchReportTermInstances(baseApiPath, params));
      }
      dispatch(showErrorModal(error));
    } finally {
      dispatch(removeFromJobQueue("cancelShreddingReportReview"));
    }
  };

export const unlockShreddingReport =
  ({ baseApiPath, reportId }) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("unlockShreddingReport"));

    const url = `${baseApiPath}/report/${reportId}/unlock`;

    try {
      const response = await localhost.get(url);

      if (response?.status === 200) {
        const state = getState();

        const hiddenParagraphs = selectHiddenParagraphs(state);
        const isByParagraph = selectIsByParagraph(state);

        const params = { isByParagraph, hiddenParagraphs, reportId };
        dispatch(fetchReportTermInstances(baseApiPath, params));
        dispatch(setHasUnsavedChanges(false));
      }

      return response;
    } catch (error) {
      dispatch(showErrorModal(error, "Failed to unlock report"));
      return null;
    } finally {
      dispatch(removeFromJobQueue("unlockShreddingReport"));
    }
  };

export const exportShredToExcel =
  ({ baseApiPath, reportId, exportType, criticalityFilters }) =>
  (dispatch) => {
    const onExport = (shredOptionsRequest) =>
      dispatch(
        executeShredExport({
          baseApiPath,
          reportId,
          exportType,
          shredOptionsRequest,
          criticalityFilters,
        })
      );

    return onExport;
  };

const ANNOTATED_DOC_DEFAULT_ERROR =
  "Failed to generate the annotated document. Please try again.";

export const exportShredAnnotatedDocument = createAsyncThunk(
  "shredding/detail/exportShredAnnotatedDocument",
  async (
    { baseApiPath, reportId, criticalityFilters, categoryAsCommentAuthor },
    { rejectWithValue }
  ) => {
    try {
      const { data: jobStatus } = await localhost.post(
        `${baseApiPath}/report/${reportId}/export/annotatedDocument`,
        { criticalityFilters, categoryAsCommentAuthor }
      );
      const jobMetaInfo = await awaitJob(jobStatus.jobId, {
        defaultErrorMessage: ANNOTATED_DOC_DEFAULT_ERROR,
      });
      const fileUrl = jobMetaInfo?.file;
      if (fileUrl) {
        download(fileUrl);
      }
      return fileUrl;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const executeShredExport =
  ({
    baseApiPath,
    reportId,
    exportType,
    shredOptionsRequest,
    criticalityFilters,
  }) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));

    const state = getState();
    const hiddenParagraphs = selectHiddenParagraphs(state);
    const isByParagraph = selectIsByParagraph(state);

    const enrichedRequest = {
      ...shredOptionsRequest,
      hiddenParagraphs,
      criticalityFilters,
      splitBySentence: !isByParagraph,
    };

    const url = `${baseApiPath}/report/${reportId}/export/${exportType}`;

    try {
      const response = await localhost.post(url, enrichedRequest);
      dispatch(hideModal(modalTypes.SHRED_TO_EXCEL));
      const jobId = response.data.jobId;
      dispatch(pollJobStatus(jobId));
      dispatch(addToJobQueue("genericJob"));
    } catch (error) {
      dispatch(showErrorModal(error));
      dispatch(clearJobQueue());
    }
  };
